import React, { useEffect, useState, useCallback } from "react";
import { Modal, Spinner, Alert, Button } from "react-bootstrap";
import GridReportService from "../../../_services/GridReportService";
import { ActiveFilter } from "../types";

interface InterpretModalProps {
  show: boolean;
  onHide: () => void;
  reportType: string;
  xDimension: string;
  yDimension: string;
  startDate?: string;
  endDate?: string;
  filters?: ActiveFilter[];
}

interface InterpretResult {
  interpretation: string;
  metadata: {
    model: string;
    processingTimeMs: number;
    dataSize: { rows: number; cols: number; legends: number };
    truncated: boolean;
  };
}

/** Simple renderer for the LLM markdown output (## headers, - bullets, paragraphs) */
function renderMarkdown(text: string): React.ReactNode {
  const lines = text.split("\n");
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`ul-${elements.length}`} style={{ margin: "6px 0", paddingLeft: 20 }}>
          {listItems.map((item, i) => (
            <li key={i} style={{ marginBottom: 4 }}>{item}</li>
          ))}
        </ul>
      );
      listItems = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const trimmed = line.trim();

    if (trimmed.startsWith("## ")) {
      flushList();
      elements.push(
        <h6
          key={`h-${i}`}
          style={{
            fontWeight: 700,
            fontSize: "0.92rem",
            marginTop: i > 0 ? 16 : 4,
            marginBottom: 6,
            color: "var(--text-strong)",
          }}
        >
          {trimmed.slice(3)}
        </h6>
      );
    } else if (trimmed.startsWith("- ")) {
      listItems.push(trimmed.slice(2));
    } else if (trimmed === "") {
      flushList();
    } else {
      flushList();
      elements.push(
        <p key={`p-${i}`} style={{ margin: "4px 0", lineHeight: 1.5 }}>
          {trimmed}
        </p>
      );
    }
  }
  flushList();

  return <>{elements}</>;
}

const InterpretModal: React.FC<InterpretModalProps> = ({
  show,
  onHide,
  reportType,
  xDimension,
  yDimension,
  startDate,
  endDate,
  filters,
}) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<InterpretResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchInterpretation = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await GridReportService.interpretReport({
        reportType,
        xDimension,
        yDimension,
        startDate,
        endDate,
        filters,
      });
      setResult(data);
    } catch (err: any) {
      const status = err.response?.status;
      if (status === 429) {
        setError(
          "You have reached your AI usage limit. Please try again later."
        );
      } else {
        setError(
          err.response?.data?.error ||
            err.message ||
            "Failed to interpret report"
        );
      }
    } finally {
      setLoading(false);
    }
  }, [reportType, xDimension, yDimension, startDate, endDate, filters]);

  useEffect(() => {
    if (show) {
      fetchInterpretation();
    }
  }, [show, fetchInterpretation]);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header>
        <Modal.Title style={{ fontSize: "1rem", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: "1.1rem" }}>&#10022;</span>
          AI Report Interpretation
        </Modal.Title>
        <button
          type="button"
          onClick={onHide}
          aria-label="Close"
          style={{
            background: "transparent",
            border: "none",
            fontSize: "1.4rem",
            lineHeight: 1,
            cursor: "pointer",
            padding: "4px 8px",
            color: "var(--text-strong)",
            opacity: 0.5,
          }}
        >
          &times;
        </button>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflow: "auto", fontSize: "0.85rem" }}>
        {loading && (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spinner
              animation="border"
              style={{ color: "var(--brand-primary)", width: 32, height: 32 }}
            />
            <div
              style={{
                marginTop: 12,
                fontSize: "0.9rem",
                color: "var(--text-body)",
                fontWeight: 500,
              }}
            >
              Analyzing report data with AI...
            </div>
            <div
              style={{
                marginTop: 4,
                fontSize: "0.78rem",
                color: "var(--text-muted)",
              }}
            >
              This may take 10-20 seconds
            </div>
          </div>
        )}

        {error && (
          <Alert variant="danger" style={{ fontSize: "0.85rem" }}>
            {error}
          </Alert>
        )}

        {!loading && result && (
          <div style={{ lineHeight: 1.6 }}>
            {renderMarkdown(result.interpretation)}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer style={{ justifyContent: "space-between", padding: "8px 16px" }}>
        <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>
          {result && (
            <>
              {result.metadata.dataSize.rows} rows x{" "}
              {result.metadata.dataSize.cols} cols
              {result.metadata.dataSize.legends > 1 &&
                ` x ${result.metadata.dataSize.legends} legends`}
              {" | "}
              {(result.metadata.processingTimeMs / 1000).toFixed(1)}s
              {result.metadata.truncated && " | Data was summarized for AI"}
            </>
          )}
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={onHide}
          style={{ padding: "4px 16px", fontSize: "0.85rem" }}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InterpretModal;
